import axios from 'axios';
import { getBackendBaseUrl } from '../lib/backendUrl';
import { setAuthNotice, SESSION_ENDED_NOTICE } from '../lib/authNotice';

const API_BASE_URL = getBackendBaseUrl();

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('inspire_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A stale/expired/invalid token must never look "logged in" to the app —
// clear it and force back to login on any 401, except the login calls
// themselves (those 401s mean "wrong password", handled by the caller).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes('/login');
    if (error.response?.status === 401 && !isLoginRequest && !error.config?.skipAuthRedirect) {
      localStorage.removeItem('inspire_token');
      localStorage.removeItem('inspire_user');
      setAuthNotice(SESSION_ENDED_NOTICE);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  studentRegister: (data) => api.post('/auth/student/register', data),
  studentLogin: (data) => api.post('/auth/student/login', data),
  facultyLogin: (data) => api.post('/auth/faculty/login', data),
  adminLogin: (data) => api.post('/admin/login', data),
  getMe: (config) => api.get('/auth/me', config),
  getStudentProfile: () => api.get('/auth/student/profile'),
  updateStudentProfile: (data) => api.patch('/auth/student/profile', data),
  uploadMyPhoto: (formData) => api.post('/students/me/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getStudentPhoto: (studentId) => api.get(`/students/${studentId}/photo`, { responseType: 'blob' }),
};

// Student Exam APIs
export const studentExamApi = {
  getAvailableExams: () => api.get('/student/exams'),
  resolveExamLink: (examToken) => api.get(`/student/exam-links/${examToken}`),
  getExam: (examId) => api.get(`/student/exams/${examId}`),
  startExam: (examId) => api.post(`/student/exams/${examId}/start`),
  saveAnswer: (attemptId, data) => api.post(`/student/attempts/${attemptId}/answers`, data),
  submitExam: (attemptId) => api.post(`/student/attempts/${attemptId}/submit`),
  uploadAnswer: (attemptId, formData) => api.post(`/student/attempts/${attemptId}/upload-answer`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMyAttempts: () => api.get('/student/attempts'),
  getAttemptDetail: (attemptId) => api.get(`/student/attempts/${attemptId}`),
  getMyExamSubmissions: (examId) => api.get(`/student/exams/${examId}/my-submissions`),
  getMyResults: () => api.get('/student/my-results'),
  downloadMySubmission: (submissionId) => api.get(`/student/submissions/${submissionId}/download`, { responseType: 'blob' }),
  downloadCorrectedPaper: (attemptId) => api.get(`/student/attempts/${attemptId}/corrected-paper`, { responseType: 'blob' }),
  getQuestionPdf: (examId) => api.get(`/student/exams/${examId}/question-pdf`, { responseType: 'blob' }),
  getTimeRemaining: (examId) => api.get(`/student/exams/${examId}/time-remaining`),
  getMyLedger: () => api.get('/student/ledger'),
  getAttendanceToday: () => api.get('/student/attendance-today'),
};

export const publicExamApi = {
  getExam: (examToken) => api.get(`/public/exams/${examToken}`),
  startExam: (examToken, data) => api.post(`/public/exams/${examToken}/start`, data),
  getSession: (attemptToken) => api.get(`/public/attempts/${attemptToken}`),
  recordViolation: (attemptToken, data) => api.post(`/public/attempts/${attemptToken}/violations`, data),
  submitExam: (attemptToken) => api.post(`/public/attempts/${attemptToken}/submit`),
  uploadAnswer: (attemptToken, formData) => api.post(`/public/attempts/${attemptToken}/upload-answer`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getResult: (resultToken) => api.get(`/public/results/${resultToken}`),
};

// Canonical subject list as {id, name} — the single source for subject pickers
export const subjectApi = {
  getOptions: () => api.get('/subjects/options'),
};

// Faculty Exam APIs
export const facultyExamApi = {
  getSubjects: () => api.get('/subjects'),
  createExam: (data) => api.post('/exams', data),
  getMyExams: () => api.get('/exams'),
  getExam: (examId) => api.get(`/exams/${examId}`),
  updateExam: (examId, data) => api.patch(`/exams/${examId}`, data),
  deleteExam: (examId) => api.delete(`/exams/${examId}`),
  uploadPdf: (examId, formData) => api.post(`/exams/${examId}/upload-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  addQuestion: (examId, data) => api.post(`/exams/${examId}/questions`, data),
  getExamSubmissions: (examId) => api.get(`/exams/${examId}/submissions`),
  downloadAllSubmissions: (examId) => api.get(`/exams/${examId}/submissions/download-all`, {
    responseType: 'blob'
  }),
  getPendingEvaluations: () => api.get('/evaluations/pending'),
  evaluateSubmission: (attemptId, data) => api.post(`/evaluations/${attemptId}`, data),
  uploadReviewedPaper: (attemptId, formData) => api.post(`/evaluations/${attemptId}/upload-reviewed`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listStudents: () => api.get('/students'),
  listGroups: () => api.get('/faculty/groups'),
  getAssignments: (examId) => api.get(`/exams/${examId}/assignments`),
  assignTargets: (examId, payload) => api.post(`/exams/${examId}/assignments`, payload),
  revokeAssignment: (examId, assignmentId) => api.delete(`/exams/${examId}/assignments/${assignmentId}`),
  extendAssignment: (examId, assignmentId, minutes) => api.patch(`/faculty/exams/${examId}/assignments/${assignmentId}/extend`, { minutes }),
  reopenAssignment: (examId, assignmentId, minutes) => api.patch(`/faculty/exams/${examId}/assignments/${assignmentId}/reopen`, { minutes }),
  downloadSubmission: (submissionId) => api.get(`/faculty/submissions/${submissionId}/download`, { responseType: 'blob' }),
  deleteSubmission: (submissionId) => api.delete(`/faculty/submissions/${submissionId}`),
  downloadCorrectedPaper: (attemptId) => api.get(`/faculty/attempts/${attemptId}/corrected-paper/download`, { responseType: 'blob' }),
  getQuestionPdf: (examId) => api.get(`/faculty/exams/${examId}/question-pdf`, { responseType: 'blob' }),
  toggleSubmissionsOpen: (examId, submissionsOpen) => api.patch(`/faculty/exams/${examId}/submissions-open`, { submissions_open: submissionsOpen }),
  getStudentLedger: (studentId) => api.get(`/faculty/students/${studentId}/ledger`),
  // Offline (paper) exams
  getOfflineRoster: (examId) => api.get(`/faculty/exams/${examId}/offline-roster`),
  saveOfflineEvaluation: (examId, assignmentId, formData) =>
    api.post(`/faculty/exams/${examId}/assignments/${assignmentId}/offline-evaluation`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  markOfflineAbsent: (examId, assignmentId) => api.post(`/faculty/exams/${examId}/assignments/${assignmentId}/absent`),
  undoOfflineAbsent: (examId, assignmentId) => api.delete(`/faculty/exams/${examId}/assignments/${assignmentId}/absent`),
};

export const configApi = {
  getUploadLimits: () => api.get('/config/upload-limits'),
};

export const attendanceApi = {
  getScopedStudents: (groupId) => api.get('/attendance/students', { params: groupId ? { group_id: groupId } : {} }),
  getByDate: (date, groupId) => api.get('/attendance', { params: groupId ? { date, group_id: groupId } : { date } }),
  markBulk: (date, records) => api.post('/attendance/bulk', { date, records }),
};

export const notificationApi = {
  list: (page = 1, pageSize = 20, type) => api.get('/notifications', { params: { page, page_size: pageSize, ...(type ? { type } : {}) } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (notificationId) => api.patch(`/notifications/${notificationId}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

export const broadcastApi = {
  send: (data) => api.post('/broadcasts', data),
  getEligibleGroups: () => api.get('/broadcasts/eligible-groups'),
};

export const classEntryApi = {
  create: (data) => api.post('/faculty/class-entries', data),
  list: (params = {}) => api.get('/faculty/class-entries', { params }),
  get: (entryId) => api.get(`/faculty/class-entries/${entryId}`),
  update: (entryId, data) => api.patch(`/faculty/class-entries/${entryId}`, data),
  remove: (entryId) => api.delete(`/faculty/class-entries/${entryId}`),
  uploadFile: (entryId, formData) => api.post(`/faculty/class-entries/${entryId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadFile: (entryId, fileId) => api.get(`/faculty/class-entries/${entryId}/files/${fileId}/download`, {
    responseType: 'blob'
  }),
  deleteFile: (entryId, fileId) => api.delete(`/faculty/class-entries/${entryId}/files/${fileId}`),
  getHomework: (entryId) => api.get(`/faculty/class-entries/${entryId}/homework`),
  markHomework: (entryId, marks) => api.post(`/faculty/class-entries/${entryId}/homework`, { marks }),
  saveHomeworkFeedback: (entryId, studentId, feedback) => api.put(`/faculty/class-entries/${entryId}/homework/${studentId}/feedback`, { feedback }),
  downloadStudentHomework: (entryId, submissionId) => api.get(`/faculty/class-entries/${entryId}/homework/${submissionId}/download`, { responseType: 'blob' }),
  // Student — entries for the student's groups only (server-scoped by JWT)
  listForStudent: (params = {}) => api.get('/student/class-entries', { params }),
  getMyHomework: (entryId) => api.get(`/student/class-entries/${entryId}/homework`),
  uploadMyHomework: (entryId, formData) => api.post(`/student/class-entries/${entryId}/homework`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteMyHomework: (entryId, submissionId) => api.delete(`/student/class-entries/${entryId}/homework/${submissionId}`),
  downloadMyHomework: (entryId, submissionId) => api.get(`/student/class-entries/${entryId}/homework/${submissionId}/download`, {
    responseType: 'blob'
  }),
  downloadFileAsStudent: (entryId, fileId) => api.get(`/student/class-entries/${entryId}/files/${fileId}/download`, {
    responseType: 'blob'
  }),
};

export const scheduleBlockApi = {
  list: () => api.get('/faculty/schedule-blocks'),
  create: (data) => api.post('/faculty/schedule-blocks', data),
  update: (blockId, data) => api.patch(`/faculty/schedule-blocks/${blockId}`, data),
  remove: (blockId) => api.delete(`/faculty/schedule-blocks/${blockId}`),
};

export const adminApi = {
  listStudents: (params = {}) => api.get('/admin/students', { params }),
  createStudent: (data) => api.post('/admin/students', data),
  updateStudent: (studentId, data) => api.patch(`/admin/students/${studentId}`, data),
  approveStudent: (studentId) => api.patch(`/admin/students/${studentId}/approve`),
  rejectStudent: (studentId) => api.patch(`/admin/students/${studentId}/reject`),
  deactivateStudent: (studentId) => api.patch(`/admin/students/${studentId}/deactivate`),
  reactivateStudent: (studentId) => api.patch(`/admin/students/${studentId}/reactivate`),
  getStudentLedger: (studentId) => api.get(`/admin/students/${studentId}/ledger`),
  downloadSubmission: (submissionId) => api.get(`/admin/submissions/${submissionId}/download`, { responseType: 'blob' }),
  downloadReviewed: (attemptId) => api.get(`/admin/reviewed/${attemptId}/download`, { responseType: 'blob' }),
  resetStudentPassword: (studentId, data) => api.post(`/admin/students/${studentId}/reset-password`, data),
  changePassword: (data) => api.post('/admin/change-password', data),
  uploadStudentPhoto: (studentId, formData) => api.post(`/admin/students/${studentId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getStudentPhoto: (studentId) => api.get(`/students/${studentId}/photo`, { responseType: 'blob' }),
  listGroups: () => api.get('/admin/groups'),
  createGroup: (data) => api.post('/admin/groups', data),
  updateGroup: (groupId, data) => api.patch(`/admin/groups/${groupId}`, data),
  deleteGroup: (groupId) => api.delete(`/admin/groups/${groupId}`),
  listGroupMembers: (groupId) => api.get(`/admin/groups/${groupId}/members`),
  listFormerGroupMembers: (groupId) => api.get(`/admin/groups/${groupId}/members/former`),
  addGroupMembers: (groupId, studentIds) => api.post(`/admin/groups/${groupId}/members`, { student_ids: studentIds }),
  markGroupMemberLeft: (groupId, studentId) => api.delete(`/admin/groups/${groupId}/members/${studentId}`),
  removeGroupMemberAddedInError: (groupId, studentId) => api.delete(`/admin/groups/${groupId}/members/${studentId}/added-in-error`),
  listGroupFaculty: (groupId) => api.get(`/admin/groups/${groupId}/faculty`),
  addGroupFaculty: (groupId, facultyIds) => api.post(`/admin/groups/${groupId}/faculty`, { faculty_ids: facultyIds }),
  removeGroupFaculty: (groupId, facultyId) => api.delete(`/admin/groups/${groupId}/faculty/${facultyId}`),
  listExams: () => api.get('/admin/exams'),
  getExamSubmissions: (examId) => api.get(`/admin/exams/${examId}/submissions`),
  getQuestionPdf: (examId) => api.get(`/admin/exams/${examId}/question-pdf`, { responseType: 'blob' }),
  getAttendance: (params) => api.get('/admin/attendance', { params }),
  getStudent: (studentId) => api.get(`/admin/students/${studentId}`),
  getStats: () => api.get('/admin/stats'),
  listPayments: (studentId) => api.get(`/admin/students/${studentId}/payments`),
  recordPayment: (studentId, data) => api.post(`/admin/students/${studentId}/payments`, data),
  setNextDue: (studentId, nextPaymentDue) => api.patch(`/admin/students/${studentId}/next-due`, { next_payment_due: nextPaymentDue }),
  listClassEntries: (params = {}) => api.get('/admin/class-entries', { params }),
  getClassEntry: (entryId) => api.get(`/admin/class-entries/${entryId}`),
  downloadClassEntryFile: (entryId, fileId) => api.get(`/admin/class-entries/${entryId}/files/${fileId}/download`, {
    responseType: 'blob'
  }),
  downloadStudentHomework: (entryId, submissionId) => api.get(`/admin/class-entries/${entryId}/homework/${submissionId}/download`, {
    responseType: 'blob'
  }),
  getScheduleBlocks: (params = {}) => api.get('/admin/schedule-blocks', { params }),
  getFacultyReport: (params = {}) => api.get('/admin/reports/faculty', { params }),
  getFacultyDrilldown: (facultyId, params = {}) => api.get(`/admin/reports/faculty/${facultyId}`, { params }),
  getStudentReport: (studentId, params = {}) => api.get(`/admin/reports/students/${studentId}`, { params }),
};

export default api;
